import { useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import { db, mutate } from '@/runtime'
import type { Resource } from '@/db/types'
import { fmtRemind } from '@/components/RemindPicker'
import { Button, Empty, PageTitle } from '@/components/ui'
import { Phrases } from '@/components/Phrases'
import { Trash2 } from 'lucide-react'
import { AppTabs, HISTORY_TABS } from '@/components/AppTabs'

/**
 * Everything deleted, newest first. Deletes are soft, so this is the thing
 * itself waiting to come back, not a copy: Restore brings it and the links it
 * had with it. Rows are purged 30 days after deletion, and the Trash says so
 * rather than pretending to hold them forever.
 */
const KINDS: Record<string, { label: string; to: string; icon: string }> = {
  Task: { label: 'Task', to: '/tasks/$id', icon: '○' },
  Project: { label: 'Project', to: '/projects/$id', icon: '▣' },
  Page: { label: 'Page', to: '/pages/$id', icon: '📄' },
  List: { label: 'List', to: '/lists/$id', icon: '☑' },
}

export const TrashView = () => {
  const [busy, setBusy] = useState<string | null>(null)
  const gone =
    useLiveQuery(async () => {
      const rows = (await db.resources.toArray()) as Resource[]
      return rows
        .filter((r) => r.deleted_at && KINDS[r.kind])
        .sort((a, b) => (a.deleted_at! < b.deleted_at! ? 1 : -1))
        .slice(0, 200)
    }, []) ?? []
  const restore = async (r: Resource) => {
    setBusy(r.id)
    try {
      await mutate.restore(r.id)
    } finally {
      setBusy(null)
    }
  }
  return (
    <div data-testid="trash">
      <PageTitle>History</PageTitle>
      <AppTabs tabs={HISTORY_TABS} label="History" />
      <p className="text-sm text-faint -mt-2 mb-3">
        Anything deleted can come back from here, with what it was linked to, for thirty days.
      </p>
      {gone.length === 0 ? (
        <Empty icon={<Trash2 size={30} strokeWidth={1.5} aria-hidden />}>Nothing deleted.</Empty>
      ) : (
        <>
          <ul className="group-list" data-testid="trash-list">
            {gone.map((r) => {
              const k = KINDS[r.kind]!
              return (
                <li
                  key={r.id}
                  className="px-3.5 py-2.5 flex items-center justify-between gap-3"
                  data-trash-id={r.id}
                >
                  <span className="min-w-0">
                    <span className="block text-[15px] truncate">
                      <span className="text-faint mr-1.5" aria-hidden>
                        {k.icon}
                      </span>
                      {r.name || 'Untitled'}
                    </span>
                    <Phrases
                      as="span"
                      className="flex text-xs text-faint"
                      parts={[k.label, r.deleted_at && `deleted ${fmtRemind(r.deleted_at)}`]}
                    />
                  </span>
                  <Button
                    variant="chip"
                    onClick={() => void restore(r)}
                    disabled={busy === r.id}
                    data-testid={`trash-restore-${r.id}`}
                  >
                    {busy === r.id ? 'Restoring…' : 'Restore'}
                  </Button>
                </li>
              )
            })}
          </ul>
        </>
      )}
    </div>
  )
}
